import React, { useState, useEffect } from 'react';
import './App.css';

const MIRRORS = [
  { label: 'Freedium Mirror',  value: 'freedium_mirror',base: 'https://freedium-mirror.cfd/' },
  { label: 'Freedium',         value: 'freedium',       base: 'https://freedium.cfd/' },
  { label: 'Freedium Mirror Web', value: 'freedium_web', base: 'https://freedium-mirror-web.vercel.app/read?url=' },
  { label: 'Archive.is',       value: 'archive',        base: 'https://archive.is/newest/' },
  { label: 'Archive.is (Alt)', value: 'archive_alt',    base: 'https://archive.is/oldest/' },
];

const PUBLISHERS = [
  'Medium', 'The New York Times', 'The Washington Post', 'Bloomberg',
  'Reuters', 'The Economist', 'Financial Times',
];

const HISTORY_KEY = 'history';
const BOOKMARKS_KEY = 'bookmarks';
const MAX_HISTORY = 50;

// localStorage throws outright in some privacy modes, so every access is guarded.
const readStore = (key) => {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const writeStore = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) { /* quota or blocked storage: the page still works without it */ }
};

const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch (e) {
    return '';
  }
};

const formatDate = (ts) =>
  new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

function App() {
  const [url, setUrl] = useState('');
  const [mirror, setMirror] = useState(() => {
    try {
      return localStorage.getItem('mirror') || 'freedium_mirror';
    } catch (e) {
      return 'freedium_mirror';
    }
  });
  const [stats, setStats] = useState({ downloads: null, stars: null });
  const [history, setHistory] = useState(() => readStore(HISTORY_KEY));
  const [bookmarks, setBookmarks] = useState(() => readStore(BOOKMARKS_KEY));
  const [tab, setTab] = useState('recent');

  useEffect(() => {
    try {
      localStorage.setItem('mirror', mirror);
    } catch (e) { /* ignore */ }
  }, [mirror]);

  useEffect(() => { writeStore(HISTORY_KEY, history); }, [history]);
  useEffect(() => { writeStore(BOOKMARKS_KEY, bookmarks); }, [bookmarks]);

  useEffect(() => {
    const fetchGitHubStats = async () => {
      try {
        const response = await fetch('/api/github-stats');
        if (response.ok) {
          const data = await response.json();
          setStats({ downloads: data.downloads || 0, stars: data.stars || 0 });
        }
      } catch (error) {
        console.error('Error loading GitHub stats:', error);
      }
    };
    fetchGitHubStats();
  }, []);

  const openArticle = (articleUrl) => {
    const selected = MIRRORS.find(m => m.value === mirror) || MIRRORS[0];
    // Mirrors that take the article as a query parameter need it encoded.
    const target = selected.base.endsWith('=')
      ? selected.base + encodeURIComponent(articleUrl)
      : selected.base + articleUrl;
    window.open(target, '_blank');
  };

  const handleUnlock = () => {
    const inputUrl = url.trim();
    if (!inputUrl) {
      alert('Please enter a URL');
      return;
    }
    openArticle(inputUrl);
    setHistory(prev => [
      { url: inputUrl, host: hostOf(inputUrl), ts: Date.now() },
      ...prev.filter(item => item.url !== inputUrl),
    ].slice(0, MAX_HISTORY));
    setUrl('');
  };

  // Clipboard reads need a user gesture and a secure context, and Safari prompts.
  // Hence a button rather than a read on mount, and a silent failure if refused.
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text && text.trim()) setUrl(text.trim());
    } catch (e) { /* denied or unsupported: the user can still paste manually */ }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleUnlock();
  };

  const isBookmarked = (articleUrl) => bookmarks.some(b => b.url === articleUrl);

  const toggleBookmark = (item) => {
    setBookmarks(prev => prev.some(b => b.url === item.url)
      ? prev.filter(b => b.url !== item.url)
      : [{ ...item, ts: item.ts || Date.now() }, ...prev]);
  };

  const removeItem = (item) => {
    if (tab === 'recent') setHistory(prev => prev.filter(h => h.url !== item.url));
    else setBookmarks(prev => prev.filter(b => b.url !== item.url));
  };

  const handleStarRepo = () => window.open('https://github.com/inulute/freedium-app', '_blank');
  const handleDownload = () => window.open('https://github.com/inulute/freedium-app/releases/latest', '_blank');
  const handleSupport = () => window.open('https://support.inulute.com', '_blank');

  const listed = tab === 'recent' ? history : bookmarks;

  return (
    <div className="App">
      {/* Top Bar */}
      <div className="top-bar">
        <div className="top-bar-buttons">
          <button className="icon-button" onClick={handleSupport} title="Support">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12,21.35l-1.45,-1.32C5.4,15.36 2,12.28 2,8.5 2,5.42 4.42,3 7.5,3c1.74,0 3.41,0.81 4.5,2.09C13.09,3.81 14.76,3 16.5,3 19.58,3 22,5.42 22,8.5c0,3.78 -3.4,6.86 -8.55,11.54L12,21.35z"/>
            </svg>
          </button>
          <button className="icon-button github-button" onClick={handleStarRepo} title="Star on GitHub">
            <svg width="20" height="20" viewBox="0 0 98 96" fill="currentColor">
              <path fillRule="evenodd" d="M48.854,0C21.839,0 0,22 0,49.217c0,21.756 13.993,40.172 33.405,46.69 2.427,0.49 3.316,-1.059 3.316,-2.362 0,-1.141 -0.08,-5.052 -0.08,-9.127 -13.59,2.934 -16.42,-5.867 -16.42,-5.867 -2.184,-5.704 -5.42,-7.17 -5.42,-7.17 -4.448,-3.015 0.324,-3.015 0.324,-3.015 4.934,0.326 7.523,5.052 7.523,5.052 4.367,7.496 11.404,5.378 14.235,4.074 0.404,-3.178 1.699,-5.378 3.074,-6.6 -10.839,-1.141 -22.243,-5.378 -22.243,-24.283 0,-5.378 1.94,-9.778 5.014,-13.2 -0.485,-1.222 -2.184,-6.275 0.486,-13.038 0,0 4.125,-1.304 13.426,5.052a46.97,46.97 0,0 1,12.214 -1.63c4.125,0 8.33,0.571 12.213,1.63 9.302,-6.356 13.427,-5.052 13.427,-5.052 2.67,6.763 0.97,11.816 0.485,13.038 3.155,3.422 5.015,7.822 5.015,13.2 0,18.905 -11.404,23.06 -22.324,24.283 1.78,1.548 3.316,4.481 3.316,9.126 0,6.6 -0.08,11.897 -0.08,13.526 0,1.304 0.89,2.853 3.316,2.364 19.412,-6.52 33.405,-24.935 33.405,-46.691C97.707,22 75.788,0 48.854,0z"/>
            </svg>
            {stats.stars !== null && (
              <span className="github-stars">{stats.stars.toLocaleString()}</span>
            )}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="container">
        {/* Hero Section */}
        <h1 className="hero-title">Medium<br/>Unlocker.</h1>
        <p className="subtitle">
          Break free from paywalls.<br/>
          Read articles from seven leading publishers without a subscription.
        </p>

        <div className="rename-notice">
          <span className="rename-badge">New</span>
          <span>
            The Android app is now called <strong>Freedium</strong> — same app,
            same data, and it now opens links from all seven publishers.
          </span>
        </div>

        <div className="publisher-row">
          {PUBLISHERS.map(name => (
            <span className="publisher-chip" key={name}>{name}</span>
          ))}
        </div>

        {/* Input Card */}
        <div className="input-card">
          <div className="input-wrapper">
            <input
              type="text"
              placeholder="Paste article URL here..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyPress={handleKeyPress}
              className="url-input"
            />
            {url ? (
              <button className="clear-button" onClick={() => setUrl('')}>✕</button>
            ) : (
              <button className="paste-button" onClick={handlePaste} title="Paste from clipboard">
                Paste
              </button>
            )}
          </div>

          <div className="options-wrapper">
            <label htmlFor="mirror-select">Mirror</label>
            <select
              id="mirror-select"
              value={mirror}
              onChange={(e) => setMirror(e.target.value)}
              className="mirror-select"
            >
              {MIRRORS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          <div className="button-group">
            <button className="download-button" onClick={handleDownload}>
              <div className="button-content">
                <img src="/android-4.svg" alt="Android" width="auto" height="25" style={{ marginRight: '8px', flexShrink: 0, marginTop: '-12px' }} />
                <div className="button-text">
                  <span>Download App</span>
                  {stats.downloads !== null && (
                    <span className="button-stats">{stats.downloads.toLocaleString()} downloads</span>
                  )}
                </div>
              </div>
            </button>
            <button className="unlock-button" onClick={handleUnlock}>
              Unlock Article
            </button>
          </div>
        </div>

        {/* Recent & Saved */}
        {(history.length > 0 || bookmarks.length > 0) && (
          <div className="library-card">
            <div className="library-tabs">
              <button
                className={tab === 'recent' ? 'library-tab active' : 'library-tab'}
                onClick={() => setTab('recent')}
              >
                Recent
              </button>
              <button
                className={tab === 'saved' ? 'library-tab active' : 'library-tab'}
                onClick={() => setTab('saved')}
              >
                Saved
              </button>
            </div>

            {listed.length === 0 ? (
              <p className="library-empty">
                {tab === 'recent' ? 'Nothing opened yet.' : 'Nothing saved yet.'}
              </p>
            ) : (
              <ul className="library-list">
                {listed.map(item => (
                  <li className="library-item" key={item.url}>
                    <button className="library-open" onClick={() => openArticle(item.url)}>
                      <span className="library-host">{item.host || 'article'}</span>
                      <span className="library-meta">{formatDate(item.ts)}</span>
                    </button>
                    <button
                      className={isBookmarked(item.url) ? 'library-action saved' : 'library-action'}
                      onClick={() => toggleBookmark(item)}
                      title={isBookmarked(item.url) ? 'Remove from saved' : 'Save'}
                    >
                      ★
                    </button>
                    <button
                      className="library-action"
                      onClick={() => removeItem(item)}
                      title="Remove"
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="library-note">Stored in this browser only — not synced with the app.</p>
          </div>
        )}

        {/* Info Card */}
        <div className="info-card">
          <h3>How it works</h3>
          <ul>
            <li>Paste an article URL from any supported publisher above</li>
            <li>Click "Unlock Article" to open it via the selected mirror</li>
            <li>Freedium mirrors cover the seven publishers; Archive.is works more widely</li>
            <li>Download the Android app to open links straight from your share sheet</li>
            <li>Enjoy unlimited reading!</li>
          </ul>
        </div>

        {/* Footer */}
        <p className="powered-by">Made with ♥ by inulute</p>
      </div>
    </div>
  );
}

export default App;
